import { IMAGE_MAX_BYTES } from '@vidmakr/shared';
import { api } from './api';

/**
 * Center-crops an image file to a square and scales it to `size` px, as WebP
 * (PNG where the browser cannot encode WebP). Transparency is kept.
 */
export async function squareImage(file: File, size: number): Promise<Blob> {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file');
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('This image could not be read');
  });
  const side = Math.min(bitmap.width, bitmap.height);
  const out = Math.min(size, side);
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, out, out);
  bitmap.close();
  const encode = (type: string, quality?: number) => new Promise<Blob | null>((r) => canvas.toBlob(r, type, quality));
  let blob = await encode('image/webp', 0.9);
  if (!blob || blob.type !== 'image/webp') blob = await encode('image/png');
  if (!blob) throw new Error('This image could not be processed');
  if (blob.size > IMAGE_MAX_BYTES) throw new Error('Image is too large after resizing');
  return blob;
}

/** Uploads a prepared image to one of the /api/images endpoints and returns its URL. */
export async function uploadImage(path: string, blob: Blob): Promise<string> {
  const res = await api<{ url: string }>(`/api/images${path}`, { method: 'POST', body: blob, headers: { 'Content-Type': blob.type } });
  return res.url;
}
